import { useEffect, useRef } from "react";

/**
 * The Method intro's particle sweep ("coaster" flavor): ~1000 dust particles
 * fly across the rail and loop-the-loop through the middle of the trip. It
 * repeats while mounted, a new pass every PASS_GAP seconds and each one
 * flying the opposite way to the last, so a long scroll through the rail
 * never sits empty. (The particles' own speed is unchanged.) Plain three.js port of the reference
 * site's R3F <ParticleSweep flavor="coaster" />.
 */

const COASTER = {
  count: 1000,
  size: 1.4,
  spawnXMin: 1500,
  spawnXMax: 1900,
  exitXMin: 1500,
  exitXMax: 1900,
  spawnYRange: 40,
  durationMin: 4,
  durationMax: 4.8,
  delayRange: 0.9,
  wobbleAmpX: 4,
  wobbleAmpY: 4,
  jiggleAmpMin: 0.2,
  jiggleAmpMax: 0.6,
  jiggleFreqMin: 0.6,
  jiggleFreqMax: 1.8,
  loopRadius: 140,
  loopStart: 0.3,
  loopEnd: 0.7,
};

const MOBILE_OVERRIDES = {
  count: 600,
  size: 2,
  spawnYRange: 80,
  loopRadius: 130,
  wobbleAmpX: 12,
  wobbleAmpY: 12,
  jiggleAmpMin: 0.4,
  jiggleAmpMax: 1,
};

const VERTEX = /* glsl */ `
  attribute vec2 aStart;
  attribute vec2 aEnd;
  attribute vec2 aTiming;  // (entryDelay, entryDuration)
  attribute vec4 aWobble;  // (freqX, freqY, phaseX, phaseY)
  attribute vec2 aJiggle;  // (amp, freq)

  uniform float uTime;
  uniform float uScale;
  uniform float uPixelRatio;
  uniform float uSize;
  uniform vec3 uLoop;      // (radius, loopStart, loopEnd)
  uniform float uDir;      // 1 left→right, -1 right→left
  uniform vec2 uWobbleAmp;

  varying float vAlpha;

  void main() {
    float raw = (uTime - aTiming.x) / aTiming.y;
    float alive = step(0.0, raw) * (1.0 - step(1.0, raw));
    float r = clamp(raw, 0.0, 1.0);

    // mostly linear travel, eased at both ends
    float eased = r < 0.15
      ? r * (r / 0.15)
      : (r > 0.85 ? 1.0 - (1.0 - r) * ((1.0 - r) / 0.15) : r);

    vec2 pos = mix(aStart, aEnd, eased);

    // full vertical loop between loopStart..loopEnd
    if (eased >= uLoop.y && eased <= uLoop.z) {
      float u = (eased - uLoop.y) / (uLoop.z - uLoop.y);
      float th = u * 6.2831853;
      pos.x += uDir * uLoop.x * sin(th);
      pos.y += uLoop.x * (1.0 - cos(th));
    }

    float decay = sin(eased * 3.14159265);
    pos.x += decay * uWobbleAmp.x * sin(uTime * aWobble.x + aWobble.z);
    pos.y += decay * uWobbleAmp.y * cos(uTime * aWobble.y + aWobble.w);
    pos.x += sin(uTime * aJiggle.y + aWobble.z) * aJiggle.x;
    pos.y += cos(uTime * aJiggle.y * 1.13 + aWobble.w) * aJiggle.x;

    vec4 mv = modelViewMatrix * vec4(pos, 0.0, 1.0);
    gl_Position = projectionMatrix * mv;
    gl_PointSize = uSize * 3.6 * uScale * uPixelRatio / max(-mv.z, 0.001) * alive;

    float twinkle = 0.88 + 0.12 * sin(uTime * 1.3 + aWobble.z * 4.0);
    vAlpha = alive * twinkle;
  }
`;

const FRAGMENT = /* glsl */ `
  uniform vec3 uColor;
  varying float vAlpha;

  void main() {
    float d = length(gl_PointCoord - 0.5);
    float disc = 1.0 - smoothstep(0.14, 0.5, d);
    if (disc < 0.004) discard;
    gl_FragColor = vec4(uColor, disc * vAlpha);
  }
`;

const rand = (min, max) => min + Math.random() * (max - min);

function buildGeometry(THREE, cfg, dir) {
  const n = cfg.count;
  const start = new Float32Array(2 * n);
  const end = new Float32Array(2 * n);
  const timing = new Float32Array(2 * n);
  const wobble = new Float32Array(4 * n);
  const jiggle = new Float32Array(2 * n);

  for (let i = 0; i < n; i++) {
    start[2 * i] = -dir * rand(cfg.spawnXMin, cfg.spawnXMax);
    start[2 * i + 1] = (Math.random() - 0.5) * cfg.spawnYRange;
    end[2 * i] = dir * rand(cfg.exitXMin, cfg.exitXMax);
    end[2 * i + 1] = (Math.random() - 0.5) * 200;
    timing[2 * i] = Math.random() * cfg.delayRange;
    timing[2 * i + 1] = rand(cfg.durationMin, cfg.durationMax);
    wobble[4 * i] = 1.2 + 2.4 * Math.random();
    wobble[4 * i + 1] = 1.2 + 2.4 * Math.random();
    wobble[4 * i + 2] = Math.random() * Math.PI * 2;
    wobble[4 * i + 3] = Math.random() * Math.PI * 2;
    jiggle[2 * i] = rand(cfg.jiggleAmpMin, cfg.jiggleAmpMax);
    jiggle[2 * i + 1] = rand(cfg.jiggleFreqMin, cfg.jiggleFreqMax);
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(new Float32Array(3 * n), 3));
  geo.setAttribute("aStart", new THREE.BufferAttribute(start, 2));
  geo.setAttribute("aEnd", new THREE.BufferAttribute(end, 2));
  geo.setAttribute("aTiming", new THREE.BufferAttribute(timing, 2));
  geo.setAttribute("aWobble", new THREE.BufferAttribute(wobble, 4));
  geo.setAttribute("aJiggle", new THREE.BufferAttribute(jiggle, 2));
  return geo;
}

// seconds between passes (a pass lasts ~5.7s, so neighbours still overlap a little)
const PASS_GAP = 3.75;

export default function MethodParticleSweep({ color = "#000000" }) {
  const hostRef = useRef(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    // three.js loads in its own chunk so it stays out of the main bundle
    let cancelled = false;
    let cleanup = null;
    import("../hooks/three.js").then((THREE) => {
      if (!cancelled) cleanup = mountSweep(THREE, host, color);
    });
    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, [color]);

  return <div ref={hostRef} className="m5-sweep" aria-hidden="true" />;
}

function mountSweep(THREE, host, color) {
  const cfg = window.matchMedia("(max-width: 767px)").matches
    ? { ...COASTER, ...MOBILE_OVERRIDES }
    : COASTER;

  // the sweep now runs for the whole scroll through the rail, so on a phone it has to stay cheap:
  // fewer particles, no antialiasing, 1x pixels and ~30fps, or the pinned slides stutter under it
  const phone = cfg === COASTER ? false : true;
  let renderer;
  try {
    renderer = new THREE.WebGLRenderer({ alpha: true, antialias: !phone, powerPreference: "low-power" });
  } catch {
    return null; // no WebGL: the sweep is decorative, skip it
  }
  renderer.setPixelRatio(phone ? 1 : Math.min(window.devicePixelRatio || 1, 2));
  renderer.domElement.style.display = "block";
  host.appendChild(renderer.domElement);

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(75, 1, 0.1, 2000);
  camera.position.set(0, 0, 500);

  // one flock per direction; passes alternate between them (a flock is idle between its own passes)
  const flocks = [1, -1].map((dir) => {
    const geometry = buildGeometry(THREE, cfg, dir);
    const material = new THREE.ShaderMaterial({
      vertexShader: VERTEX,
      fragmentShader: FRAGMENT,
      transparent: true,
      depthWrite: false,
      blending: THREE.NormalBlending,
      uniforms: {
        uTime: { value: -1 },
        uScale: { value: 1 },
        uPixelRatio: { value: renderer.getPixelRatio() },
        uSize: { value: cfg.size },
        uColor: { value: new THREE.Color(color) },
        uLoop: { value: new THREE.Vector3(cfg.loopRadius, cfg.loopStart, cfg.loopEnd) },
        uDir: { value: dir },
        uWobbleAmp: { value: new THREE.Vector2(cfg.wobbleAmpX, cfg.wobbleAmpY) },
      },
    });
    const points = new THREE.Points(geometry, material);
    points.frustumCulled = false;
    scene.add(points);
    return { geometry, material };
  });

  const resize = () => {
    const w = host.clientWidth || 1;
    const h = host.clientHeight || 1;
    renderer.setSize(w, h, false);
    renderer.domElement.style.width = "100%";
    renderer.domElement.style.height = "100%";
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    const scale = (0.5 * h) / Math.tan((75 * Math.PI) / 360);
    flocks.forEach((f) => (f.material.uniforms.uScale.value = scale));
  };
  resize();
  const ro = new ResizeObserver(resize);
  ro.observe(host);

  const t0 = performance.now();
  let raf = 0;
  const rootStyle = document.documentElement.style;
  let lastFrame = 0;
  const tick = (now) => {
    raf = requestAnimationFrame(tick);
    if (phone && now - lastFrame < 28) return;
    lastFrame = now;
    const t = (performance.now() - t0) / 1000;
    // flock 0 flies passes 0, 2, 4...; flock 1 passes 1, 3, 5...; a pass starts every PASS_GAP seconds
    // Method fades its background to dark on the last phase and publishes how far along as --nav-invert (0..1):
    // follow it so the particles go from black to white with the page, and stay visible
    const dark = parseFloat(rootStyle.getPropertyValue("--nav-invert")) || 0;
    flocks.forEach((f, i) => {
      const local = t - i * PASS_GAP;
      f.material.uniforms.uTime.value = local < 0 ? -1 : local % (2 * PASS_GAP);
      f.material.uniforms.uColor.value.setScalar(dark);
    });
    renderer.render(scene, camera);
  };
  raf = requestAnimationFrame(tick);

  return () => {
    cancelAnimationFrame(raf);
    ro.disconnect();
    flocks.forEach((f) => {
      f.geometry.dispose();
      f.material.dispose();
    });
    renderer.dispose();
    renderer.domElement.remove();
  };
}
